/**
 * Link classifier — decides whether an href targets:
 *   - `host-nav`    host-level route claimed by a view page or system route
 *   - `child-nav`   internal route belonging to the subapp's own router
 *   - `external`    different origin / mailto / tel / ftp / file / javascript
 *   - `ignore`      empty href, pure hash
 *
 * Pure function, framework-agnostic. Used by:
 *   - `prevent-clicks.ts`                       fallback handler for raw <a> clicks
 *   - `ProxyApiInstance.host.classifyLink(href)` exposed to vanilla subapp code
 *   - `@wippy-fe/router` RouterLink wrapper     transparent host-nav relay
 *
 * Matching strategy for host-nav:
 *   1. Compare the path's first literal segment against known system routes
 *      (`/chat`, `/c`, `/web`, `/page`, `/keeper`, `/login`, `/logout`)
 *   2. Compare against mountRoute top literal segments from ChildAppConfig.mountRoutes
 *   3. Root-mount fallback — if a view page claimed `/:part(.*)*`, it catches
 *      everything not otherwise matched
 */

export type LinkKind = 'ignore' | 'external' | 'host-nav' | 'child-nav'

export interface ClassifierContext {
  /** Map of mountRoute pattern → pageId, from ChildAppConfig */
  mountRoutes?: Record<string, string>
  /** Prefix to strip before matching (when host is mounted under e.g. `/app`) */
  routePrefix?: string
  /** Host's origin — links with a different origin classify as `external` */
  hostOrigin?: string
  /** This iframe's own pageId — excluded from mount route matching to prevent self-match */
  selfPageId?: string
}

// ─── Local Router Integration ───
// When a child iframe registers its Vue Router via setLocalRouter(), the
// classifier checks own routes FIRST. This prevents ancestor mount routes
// (especially root-mount catch-alls) from claiming the child's local routes.
interface ResolveResult { matched: Array<{ path: string }>, name?: string | symbol | null | undefined }
let _localResolve: ((path: string) => ResolveResult) | null = null

export function setLocalRouter(resolve: ((path: string) => ResolveResult) | null) {
  _localResolve = resolve
}

export interface LinkClassification {
  kind: LinkKind
  /** The original href the caller passed in (echoed for convenience) */
  href: string
  /** Path after origin + routePrefix stripping, when applicable */
  normalizedPath?: string
  /** For host-nav matched to a mountRoute, the page id of the target */
  targetPageId?: string
}

const NON_HTTP_EXTERNAL_SCHEMES = /^(?:javascript|mailto|tel|sms|ftp|file|data|blob):/i
const PROTOCOL_PREFIX = /^[a-z][a-z0-9.+-]*:/i

/**
 * System routes hardcoded in gen-2-chat's router (src/app/router/constants.ts).
 * Any link whose top segment is one of these is a host-nav target.
 */
export const SYSTEM_HOST_ROUTE_SEGMENTS: ReadonlySet<string> = new Set([
  'chat',
  'c',
  'web',
  'page',
  'keeper',
  'login',
  'logout',
])

function extractTopLiteralSegment(pattern: string): string | null {
  // `/:part(.*)*`                → null (root mount, no literal prefix)
  // `/keeper/:part(.*)*`         → 'keeper'
  // `/admin/users/:part(.*)*`    → 'admin' (top segment only)
  const match = pattern.match(/^\/([a-z0-9-]+)/i)
  return match ? match[1].toLowerCase() : null
}

function stripPrefix(path: string, prefix: string | undefined): string {
  if (!prefix)
    return path
  if (path.startsWith(prefix)) {
    const stripped = path.slice(prefix.length)
    return stripped.startsWith('/') ? stripped : `/${stripped}`
  }
  return path
}

function firstSegment(path: string): string {
  const cleaned = path.split(/[?#]/)[0]
  const segments = cleaned.split('/').filter(Boolean)
  return (segments[0] ?? '').toLowerCase()
}

export function classifyLink(
  href: string | null | undefined,
  ctx: ClassifierContext = {},
): LinkClassification {
  const safeHref = href ?? ''

  if (!safeHref || safeHref === '#')
    return { kind: 'ignore', href: safeHref }

  if (safeHref.startsWith('#'))
    return { kind: 'ignore', href: safeHref }

  if (NON_HTTP_EXTERNAL_SCHEMES.test(safeHref))
    return { kind: 'external', href: safeHref }

  // Absolute URL with http-like protocol — check origin
  let workingHref = safeHref
  if (PROTOCOL_PREFIX.test(safeHref)) {
    if (!ctx.hostOrigin)
      return { kind: 'external', href: safeHref }
    try {
      const url = new URL(safeHref)
      if (url.origin !== ctx.hostOrigin)
        return { kind: 'external', href: safeHref }
      workingHref = url.pathname + url.search + url.hash
    }
    catch {
      return { kind: 'external', href: safeHref }
    }
  }

  // At this point: working href is a path (possibly with query/hash)
  let path = workingHref
  path = stripPrefix(path, ctx.routePrefix)
  if (!path.startsWith('/'))
    path = `/${path}`

  const topSeg = firstSegment(path)

  // 0. Check own router first. If this iframe's Vue Router has a named
  //    route for the path (not a catch-all/404), it's definitively child-nav.
  //    This prevents ancestor mount routes from claiming local routes.
  if (_localResolve) {
    try {
      const resolved = _localResolve(path)
      // A real match is one that isn't a catch-all/404 route.
      // Detect catch-alls by checking if the deepest matched route's path
      // contains a wildcard pattern like `(.*)` or `:pathMatch(.*)`.
      const deepest = resolved.matched[resolved.matched.length - 1]
      const isCatchAll = !deepest || /\(\.\*\)/.test(deepest.path)
      const isRealMatch = resolved.matched.length > 0 && !isCatchAll
      if (isRealMatch)
        return { kind: 'child-nav', href: safeHref, normalizedPath: path }
    }
    catch { /* router.resolve can throw for malformed paths */ }
  }

  // 1. Explicit mount routes — they carry targetPageId metadata
  const mountRoutes = ctx.mountRoutes ?? {}
  let rootMountPageId: string | null = null
  for (const [pattern, pageId] of Object.entries(mountRoutes)) {
    // Skip self — prevents matching our own page's mount route
    if (ctx.selfPageId && pageId === ctx.selfPageId)
      continue
    const literalTop = extractTopLiteralSegment(pattern)
    if (literalTop === null) {
      rootMountPageId = pageId
      continue
    }
    if (literalTop === topSeg) {
      return {
        kind: 'host-nav',
        href: safeHref,
        normalizedPath: path,
        targetPageId: pageId,
      }
    }
  }

  // 2. System routes — host-nav without targetPageId
  if (topSeg && SYSTEM_HOST_ROUTE_SEGMENTS.has(topSeg)) {
    return { kind: 'host-nav', href: safeHref, normalizedPath: path }
  }

  // 3. Root mount catches everything not claimed by own router or system routes
  if (rootMountPageId) {
    return {
      kind: 'host-nav',
      href: safeHref,
      normalizedPath: path,
      targetPageId: rootMountPageId,
    }
  }

  return { kind: 'child-nav', href: safeHref, normalizedPath: path }
}
